import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Pencil, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Panel } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, pagamentoLabel } from "@/lib/api";

const EditAnagrafica = ({ u, onSaved }) => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  if (!f) return <Button size="sm" variant="outline" onClick={() => setF({ nome: u.nome, cognome: u.cognome, data_nascita: u.data_nascita || "", genere: u.genere || "Non indicato" })} data-testid="anagrafica-edit-button"><Pencil className="h-4 w-4 mr-1" />Correggi nome, cognome, data di nascita, genere</Button>;
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${u.id}/anagrafica`, f);
      toast.success("Anagrafica aggiornata");
      setF(null);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="border rounded-md p-3 space-y-2 bg-muted/30" data-testid="anagrafica-edit-form">
      <div className="grid sm:grid-cols-4 gap-2">
        <Input placeholder="Cognome" value={f.cognome} onChange={(e) => setF({ ...f, cognome: e.target.value })} data-testid="anagrafica-cognome-input" />
        <Input placeholder="Nome" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} data-testid="anagrafica-nome-input" />
        <Input type="date" value={f.data_nascita} onChange={(e) => setF({ ...f, data_nascita: e.target.value })} data-testid="anagrafica-data-nascita-input" />
        <Select value={f.genere} onValueChange={(v) => setF({ ...f, genere: v })}>
          <SelectTrigger data-testid="anagrafica-genere-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["Uomo", "Donna", "Non indicato"].map((g) => <SelectItem key={g} value={g} data-testid={`anagrafica-genere-option-${g.toLowerCase().replace(" ", "-")}`}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setF(null)} data-testid="anagrafica-cancel-button">Annulla</Button>
        <Button size="sm" onClick={save} disabled={saving} data-testid="anagrafica-save-button">{saving ? "Salvataggio..." : "Salva"}</Button>
      </div>
    </div>
  );
};

import { useSort, SortHead, byTessera } from "@/components/admin/SortTable";

const GETTERS = { tessera_numero: byTessera };

const UserDetail = ({ userId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const load = () => api.get(`/admin/users/${userId}`).then((r) => setD(r.data));
  useEffect(() => {
    if (userId) load();
    else setD(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  const u = d?.user;
  const remove = async () => {
    if (!window.confirm(`Cancellare definitivamente ${u.cognome} ${u.nome}? Verranno eliminate anche le sue barche e i suoi annunci.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utente cancellato");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl uppercase">{u ? `${u.cognome} ${u.nome}` : "…"}</DialogTitle></DialogHeader>
        {u && (
          <div className="space-y-5 text-sm">
            <EditAnagrafica key={u.id} u={u} onSaved={() => { load(); onChanged(); }} />
            {d.memberships.length === 0 && u.role !== "admin" && (
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 ml-2" onClick={remove} data-testid="user-delete-button"><Trash2 className="h-4 w-4 mr-1" />Cancella utente (non socio)</Button>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-muted-foreground">Nato/a il</span><div>{fmtDate(u.data_nascita)}</div></div>
              <div><span className="text-muted-foreground">Genere</span><div>{u.genere || "—"}</div></div>
              <div><span className="text-muted-foreground">Tessera FIV</span><div>{u.tessera_fiv || "—"}</div></div>
              <div><span className="text-muted-foreground">Email</span><div className="break-all">{u.email}</div></div>
              <div><span className="text-muted-foreground">Telefono</span><div>{u.telefono || "—"}</div></div>
              <div className="col-span-2"><span className="text-muted-foreground">Indirizzo</span><div>{u.indirizzo || "—"}</div></div>
            </div>
            <div>
              <div className="font-heading uppercase text-lg mb-1">Barche</div>
              {d.boats.length === 0 ? <p className="text-muted-foreground">Nessuna</p> : d.boats.map((b) => (
                <p key={b.id}>{b.categoria} · <span className="font-mono-x">{b.numero_velico}</span> — equipaggio {b.crew_cognome} {b.crew_nome}{!b.crew_confermato && " (in attesa)"}</p>
              ))}
            </div>
            <div>
              <div className="font-heading uppercase text-lg mb-1">Iscrizioni</div>
              {d.memberships.length === 0 ? <p className="text-muted-foreground">Nessuna</p> : d.memberships.map((m) => (
                <p key={m.id}>{m.anno} · {m.categoria_socio} · {pagamentoLabel(m.metodo_pagamento)} · {m.pagamento_confermato ? "pagato" : "da confermare"}</p>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const Tesserati = () => {
  const { user: me } = useAuth();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const s = useSort(items, GETTERS);
  const H = ({ k, label }) => <SortHead k={k} label={label} s={s} prefix="tesserati" />;

  const search = async (e) => {
    e?.preventDefault();
    const { data } = await api.get("/admin/users", { params: { q } });
    setItems(data);
  };

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (u) => {
    if (!window.confirm(`Cancellare definitivamente ${u.cognome} ${u.nome}? Verranno eliminate anche le sue barche e i suoi annunci.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utente cancellato");
      search();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const setRole = async (u, role) => {
    try {
      await api.put(`/admin/users/${u.id}/role`, { role });
      toast.success(`Ruolo aggiornato per ${u.cognome} ${u.nome}`);
      search();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Panel>
      <form onSubmit={search} className="flex gap-2 mb-6 max-w-xl">
        <Input placeholder="Cerca per cognome, nome, tessera FIV o n. tessera" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tesserati-search-input" />
        <Button type="submit" className="bg-[#0B192C] hover:bg-[#1E3E62]" data-testid="tesserati-search-button"><Search className="h-4 w-4 mr-1" />Cerca</Button>
      </form>
      <div className="overflow-x-auto">
        <Table data-testid="tesserati-table">
          <TableHeader>
            <TableRow><H k="cognome" label="Cognome" /><H k="nome" label="Nome" /><H k="data_nascita" label="Data nascita" /><H k="tessera_fiv" label="Tessera FIV" /><H k="tessera_numero" label="N. tessera" /><H k="role" label="Ruolo" /><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Nessun risultato</TableCell></TableRow>}
            {s.sorted.map((u) => (
              <TableRow key={u.id} data-testid={`tesserati-row-${u.id}`}>
                <TableCell className="font-medium">{u.cognome}</TableCell>
                <TableCell>{u.nome}</TableCell>
                <TableCell>{fmtDate(u.data_nascita)}</TableCell>
                <TableCell className="font-mono-x">{u.tessera_fiv || "—"}</TableCell>
                <TableCell className="font-mono-x">{u.tessera_numero || "—"}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => setRole(u, v)} disabled={u.id === me.id}>
                    <SelectTrigger className="w-40" data-testid={`tesserati-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="utente">Utente</SelectItem>
                      <SelectItem value="collaboratore">Collaboratore</SelectItem>
                      <SelectItem value="admin">Amministratore</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setDetail(u.id)} data-testid={`tesserati-detail-button-${u.id}`}>Dettagli</Button>
                  {!u.socio && !u.tessera_numero && u.role !== "admin" && (
                    <Button size="sm" variant="outline" className="ml-2 text-red-600 border-red-200 hover:bg-red-50" title="Cancella utente (non socio)" onClick={() => remove(u)} data-testid={`tesserati-delete-button-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <UserDetail userId={detail} onClose={() => setDetail(null)} onChanged={search} />
    </Panel>
  );
};
